import { useCallback, useMemo } from 'react';
import { AdviseCurrentSupplier, AdviseEngagement, SupplierLineItemsReplacement } from '../../../types/advise';
import {
  updateAdviseCurrentSupplier,
  updateAdviseInvoiceLineItems,
  updateAdviseSupplierLineItems
} from '../../../Api/Advise/Engagements';
import { CallResult } from '../../../Api/callExternalApi';
import { changeRefusal, findSupplier, NO_SUPPLIERS } from './currentSupplierRereads';
import { CurrentSuppliersQueue } from './useCurrentSuppliersQueue';

// A supplier with no entry and no invoice gets no Edit on its card to reopen these lines by.
const needsLineEntry = (supplier?: AdviseCurrentSupplier | null) =>
  supplier?.spend_entry === 'monthly_total' || (!!supplier && !supplier.spend_entry && supplier.invoices.length === 0);

type Send = (id: string) => Promise<CallResult<AdviseEngagement>>;

export const useManualSpendSaves = ({ showRefusal, sendThenShow }: CurrentSuppliersQueue) => {
  const save = useCallback(
    async (send: Send) => {
      const sent = await sendThenShow(send);
      if (sent?.error) showRefusal(changeRefusal(sent));
    },
    [sendThenShow, showRefusal]
  );

  // One queued turn, so the lines never show saved on a supplier still on its monthly total.
  const saveManualLines = useCallback(
    async (supplierId: string, invoiceId: string | null, lines: SupplierLineItemsReplacement) => {
      const switchRefused: { error: CallResult<AdviseEngagement>['error'] } = { error: null };
      await save(async (id) => {
        const linesSaved = await (invoiceId
          ? updateAdviseInvoiceLineItems(id, supplierId, invoiceId, lines)
          : updateAdviseSupplierLineItems(id, supplierId, lines));
        // Read from the answer, since a teammate's switch to a monthly total leaves the lines' version alone.
        const savedSupplier =
          linesSaved.data && findSupplier(linesSaved.data.current_suppliers ?? NO_SUPPLIERS, supplierId);
        if (!needsLineEntry(savedSupplier)) return linesSaved;
        const switched = await updateAdviseCurrentSupplier(id, supplierId, { spend_entry: 'line_items' });
        if (!switched.error) return switched;
        switchRefused.error = switched.error;
        return linesSaved;
      });
      const { error } = switchRefused;
      if (error) showRefusal(changeRefusal({ data: null, error, now: null }));
    },
    [save, showRefusal]
  );

  const saveMonthlyTotal = useCallback(
    (supplierId: string, total: number) =>
      save((id) => updateAdviseCurrentSupplier(id, supplierId, { spend_entry: 'monthly_total', monthly_total: total })),
    [save]
  );

  const countLineItems = useCallback(
    (supplierId: string) => save((id) => updateAdviseCurrentSupplier(id, supplierId, { spend_entry: 'line_items' })),
    [save]
  );

  return useMemo(
    () => ({ saveManualLines, saveMonthlyTotal, countLineItems }),
    [saveManualLines, saveMonthlyTotal, countLineItems]
  );
};
